import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import api from "../api/axiosConfig";
import { useAuth } from "./AuthContext";

const InternalPickingContext = createContext();

export const useInternalPicking = () => useContext(InternalPickingContext);

export const InternalPickingProvider = ({ children }) => {
  const [pickings, setPickings] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // --- 1. FETCH ALL ---
  const fetchPickings = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await api.get('/internal-pickings');
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setPickings(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      fetchPickings();
    } else {
      setPickings([]);
    }
  }, [user, fetchPickings]);

  // --- HELPER GENERATORS ---
  const generateTransferRef = () => `WH/INT/${Date.now().toString().slice(-5)}`;

  // --- 2. ACTIONS ---
  const getPicking = (id) => {
    return pickings.find(p => String(p.id) === String(id));
  };

  const createPicking = async (data) => {
    const tempId = `EPR${Date.now().toString().slice(-5)}`;
    const payload = {
      ...data,
      id: tempId, 
      status: "NEW"
    };

    try {
      const res = await api.post('/internal-pickings', payload);
      const savedData = res.data?.data || res.data;
      setPickings(prev => [savedData, ...prev]);
      return savedData;
    } catch (error) {
      toast.error(error.response?.data?.error || error.message);
      throw error;
    }
  };

  const updatePicking = async (id, updatedData) => {
    setPickings(prev => prev.map(p => String(p.id) === String(id) ? { ...p, ...updatedData } : p));
    try {
      await api.put(`/internal-pickings/${id}`, updatedData);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save changes to server");
      fetchPickings();
    }
  };

  const updateStatus = async (id, status) => {
    const picking = pickings.find(p => String(p.id) === String(id));
    if (!picking) return;
    await updatePicking(id, { ...picking, status });
  };

  const createPickingTransfer = (id) => {
    const transferRef = generateTransferRef();
    const picking = pickings.find(p => String(p.id) === String(id));
    
    if (picking) {
        const updatedData = { 
            ...picking, 
            status: "PICKING CREATED", 
            pickingTransferRef: transferRef 
        };
        updatePicking(id, updatedData);
    }
    return transferRef;
  };

  const markAsReceived = (id) => {
    const picking = pickings.find(p => String(p.id) === String(id));
    if (picking) {
        const updatedData = { 
            ...picking, 
            status: "RECEIVED", 
            receivedDate: new Date().toISOString().split('T')[0]
        };
        updatePicking(id, updatedData);
    }
  };

  return (
    <InternalPickingContext.Provider value={{ 
        pickings, 
        loading,
        createPicking, 
        updatePicking, 
        updateStatus, 
        createPickingTransfer, 
        markAsReceived, 
        getPicking 
    }}>
      {children}
    </InternalPickingContext.Provider>
  );
};